import React, { useState, useEffect, useMemo } from "react";
import {
  Tag,
  Calendar,
  AlertCircle,
  ChevronRight,
  Copy,
  Check,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { getMyCoupons } from "../../server/Coupon";
import { useSelector } from "react-redux";
import LoadingStatus from "../ui/LoadingStatus";
import {
  COUPON_RETURN_CHECKOUT,
  clearCouponReturn,
  getCouponReturnCheckoutStep,
  isCouponCheckoutReturn,
} from "../../utils/couponNavigation";
import { CHECKOUT_STEP } from "../../constants/checkoutSteps";

const STATUS_META = {
  ACTIVE: { label: "פעיל", className: "status-active", usable: true },
  PARTIALLY_USED: {
    label: "מומש חלקית",
    className: "status-inactive",
    usable: false,
  },
  USED: { label: "מומש", className: "status-inactive", usable: false },
  INACTIVE: { label: "מבוטל", className: "status-inactive", usable: false },
};

const FILTERS = [
  { id: "usable", label: "למימוש" },
  { id: "all", label: "הכל" },
  { id: "history", label: "היסטוריה" },
];

const isExpired = (expiredAt) => {
  if (!expiredAt) return false;
  const t = new Date(expiredAt).getTime();
  return Number.isFinite(t) && t <= Date.now();
};

const mapCpsCoupon = (coupon) => {
  const status = coupon.status || "INACTIVE";
  const expired = isExpired(coupon.expiredAt);
  const meta = STATUS_META[status] || STATUS_META.INACTIVE;
  const remaining =
    typeof coupon.sum === "number" && typeof coupon.usedSum === "number"
      ? Math.max(0, Number((coupon.sum - coupon.usedSum).toFixed(2)))
      : Number(coupon.sum) || 0;

  return {
    id: coupon.id,
    couponCode: coupon.couponCode,
    sum: Number(coupon.sum) || 0,
    usedSum: Number(coupon.usedSum) || 0,
    remaining,
    minOrder: Number(coupon.minOrder) || 0,
    expiredAt: coupon.expiredAt || "",
    status,
    expired,
    usable: meta.usable && !expired && remaining > 0,
    statusLabel: expired && status === "ACTIVE" ? "פג תוקף" : meta.label,
    statusClass:
      expired && status === "ACTIVE" ? "status-inactive" : meta.className,
  };
};

const CostumerCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("usable");
  const [copiedCode, setCopiedCode] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { user, stationId } = useSelector((state) => ({
    user: state.user.user,
    stationId: state.user.stationId,
  }));

  const fromCheckout = isCouponCheckoutReturn(location);

  const handleExit = () => {
    const checkoutStep = getCouponReturnCheckoutStep() ?? CHECKOUT_STEP.DETAILS;
    navigate("/cart", {
      state: {
        couponReturnTo: COUPON_RETURN_CHECKOUT,
        checkoutStep,
      },
    });
    clearCouponReturn();
  };

  const copyCouponCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.id && !localStorage.jwtToken) {
        setLoading(false);
        setCoupons([]);
        return;
      }
      setLoading(true);
      setLoadError("");
      try {
        const params = {};
        if (!stationId) {
          setCoupons([]);
          setLoading(false);
          return;
        }
        params.branch = String(stationId);
        const res = await getMyCoupons(params);
        if (cancelled) return;
        const list = (res.data?.coupons ?? []).map(mapCpsCoupon);
        setCoupons(list);
      } catch (e) {
        console.error("getMyCoupons failed", e);
        if (!cancelled) {
          setCoupons([]);
          setLoadError("לא ניתן לטעון את הקופונים כרגע");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.id, stationId]);

  useEffect(() => {
    if (loading || window.location.hash !== "#coupons") return;
    const section = document.getElementById("coupons");
    if (!section) return;
    requestAnimationFrame(() => {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [loading]);

  const filteredCoupons = useMemo(() => {
    if (filter === "usable") return coupons.filter((c) => c.usable);
    if (filter === "history") return coupons.filter((c) => !c.usable);
    return coupons;
  }, [coupons, filter]);

  const usableCount = coupons.filter((c) => c.usable).length;

  return (
    <div id="coupons" className="details-window">
      <div className="header-window">
        <h2>הקופונים שלי</h2>
        {fromCheckout ? (
          <button
            type="button"
            className="coupons-exit-btn"
            onClick={handleExit}
          >
            <ChevronRight size={16} strokeWidth={2.5} aria-hidden />
            חזרה להמשך ההזמנה
          </button>
        ) : null}
      </div>

      <div className="coupons-container">
        {!loading && coupons.length > 0 ? (
          <div
            className="coupons-filters"
            role="tablist"
            aria-label="סינון קופונים"
          >
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={filter === item.id}
                className={`coupons-filter-btn${filter === item.id ? " is-active" : ""}`}
                onClick={() => setFilter(item.id)}
              >
                {item.label}
                {item.id === "usable" ? (
                  <span className="coupons-filter-count">{usableCount}</span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}

        {loading ? (
          <LoadingStatus className="loading-status--compact" />
        ) : loadError ? (
          <div className="empty-message">
            <AlertCircle size={40} strokeWidth={1.5} />
            <p>{loadError}</p>
          </div>
        ) : filteredCoupons.length === 0 ? (
          <div className="empty-message">
            <AlertCircle size={40} strokeWidth={1.5} />
            <p>
              {filter === "usable"
                ? "אין קופונים זמינים למימוש כרגע"
                : filter === "history"
                  ? "אין קופונים בהיסטוריה"
                  : "אין קופונים להצגה"}
            </p>
          </div>
        ) : (
          <div className="coupons-list">
            {filteredCoupons.map((coupon) => (
              <div
                key={coupon.id}
                className={`coupon-card${!coupon.usable ? " inactive" : ""}`}
              >
                <div className="coupon-header">
                  <div className="coupon-badge">
                    <Tag size={18} strokeWidth={2} />
                    <span className="discount-text">{coupon.sum} ₪</span>
                  </div>
                  <div className="coupon-status">
                    <span className={coupon.statusClass}>
                      {coupon.statusLabel}
                    </span>
                  </div>
                </div>

                <div className="coupon-body">
                  <div className="coupon-code-row">
                    <span className="coupon-code">{coupon.couponCode}</span>
                    {coupon.usable ? (
                      <button
                        type="button"
                        className="coupon-copy-btn"
                        onClick={() => copyCouponCode(coupon.couponCode)}
                        title="העתק קוד"
                        aria-label={`העתק קוד קופון ${coupon.couponCode}`}
                      >
                        {copiedCode === coupon.couponCode ? (
                          <Check size={15} strokeWidth={2.5} />
                        ) : (
                          <Copy size={15} strokeWidth={2.5} />
                        )}
                      </button>
                    ) : null}
                  </div>
                  <span className="coupon-description">
                    למימוש החל מ-{coupon.minOrder} ₪
                  </span>
                </div>

                <div className="coupon-footer">
                  <Calendar size={14} strokeWidth={2} />
                  <span className="expiry-text">
                    {coupon.expiredAt
                      ? `בתוקף עד: ${new Date(coupon.expiredAt).toLocaleDateString("he-IL")}`
                      : "ללא תאריך תפוגה"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CostumerCoupons;
